import {useIsMutating, useMutation, useQueryClient} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {PinIcon, PinSlashIcon} from '@primer/octicons-react'
import {useTaskPins} from './use-task-pins'
import {taskPinApiPath} from '../utils/task-pins'

export function taskPinErrorMessage(error: Error) {
  if (
    error instanceof FetchCAPIError &&
    error.status === 422 &&
    (error.code === 'pin_limit_reached' || error.errors?.some(item => item.code === 'pin_limit_reached'))
  ) {
    return 'You can pin up to 20 sessions. Unpin a session before pinning another.'
  }
  if (error instanceof FetchCAPIError && error.status === 404) {
    return 'This session is no longer available to pin. It may have been deleted or you may have lost access.'
  }
  return 'Couldn’t update the pinned session. Please try again.'
}

export function useTaskPinAction({taskId, enabled = false}: {taskId?: string; enabled?: boolean}) {
  const queryClient = useQueryClient()
  const pins = useTaskPins(enabled && !!taskId)
  const mutationKey = [...pins.queryKey, taskId]
  const pending = useIsMutating({mutationKey}) > 0
  const isPinned = pins.data?.some(pin => pin.task.id === taskId) ?? false
  const mutation = useMutation({
    mutationKey,
    mutationFn: async ({pin}: {pin: boolean}) => {
      if (!taskId || !enabled || !copilotFeatureFlags.taskPinning) throw new Error('Session pinning is unavailable')
      await fetchCAPI({path: taskPinApiPath(taskId), method: pin ? 'PUT' : 'DELETE'})
    },
    retry: (count, error) => count < 2 && error instanceof FetchCAPIError && error.status === 409,
    retryDelay: attempt => 250 * 2 ** attempt,
    onSuccess: async (_data, {pin}) => {
      await queryClient.invalidateQueries({queryKey: pins.queryKey})
      announce(pin ? 'Session pinned.' : 'Session unpinned.')
    },
  })
  const action =
    enabled && taskId && copilotFeatureFlags.taskPinning
      ? {
          key: 'pin-session',
          icon: isPinned ? PinSlashIcon : PinIcon,
          label: isPinned ? 'Unpin session' : 'Pin session',
          disabled: !pins.data || pins.isError || pending,
          loading: pending,
          onSelect: (event?: React.SyntheticEvent) => {
            event?.preventDefault()
            if (!pins.data || pins.isError || queryClient.isMutating({mutationKey}) > 0) return
            mutation.mutate({pin: !isPinned})
            announce(isPinned ? 'Unpinning session.' : 'Pinning session.')
          },
        }
      : undefined

  return {
    action,
    error: mutation.error ? taskPinErrorMessage(mutation.error) : undefined,
    dismissError: mutation.reset,
  }
}
